import type { ThemeId } from '../../theme/ThemeContext'
import { AsyncPullLoadingIndicator } from '../async/AsyncPullLoadingIndicator'
import { PageAsyncEmptyState } from '../async/PageAsyncEmptyState'
import { PageAsyncErrorState } from '../async/PageAsyncErrorState'
import { ChromePrimaryButton } from '../ChromeActions'

export type WorkflowStateKind = 'loading' | 'empty' | 'error'

function EmptyWorkflowIllustration({ angular }: { angular: boolean }) {
  const rx = angular ? 0 : 6
  const sw = angular ? 1.8 : 1.5

  return (
    <svg
      className="mx-auto h-[5rem] w-[5rem] shrink-0 text-ink-soft/48"
      viewBox="0 0 80 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="geometricPrecision"
      aria-hidden
    >
      <rect x="26" y="8" width="28" height="14" rx={rx} className="stroke-current" strokeWidth={sw} strokeOpacity={0.72} />
      <rect x="8" y="50" width="26" height="14" rx={rx} className="stroke-current" strokeWidth={sw} strokeOpacity={0.55} />
      <rect x="46" y="50" width="26" height="14" rx={rx} className="stroke-current" strokeWidth={sw} strokeOpacity={0.55} />
      <path
        d="M40 22 C40 34 21 38 21 50 M40 22 C40 34 59 38 59 50"
        className="stroke-current"
        strokeWidth={sw}
        strokeOpacity={0.38}
        strokeLinecap="round"
      />
    </svg>
  )
}

type Props = {
  theme: ThemeId
  kind: WorkflowStateKind
  loadingLabel?: string
  detail?: string
  onRetry?: () => void
  onCreate?: () => void
}

/** 工作流列表：加载 / 空 / 错误占位（对齐 {@link ThemedComicState}） */
export function ThemedWorkflowState({ theme, kind, loadingLabel, detail, onRetry, onCreate }: Props) {
  const angular = theme === 'pixel' || theme === 'geek'
  const terminalish = theme === 'geek'

  if (kind === 'loading') {
    return (
      <AsyncPullLoadingIndicator
        angular={angular}
        label={loadingLabel ?? (terminalish ? 'loading_flows…' : '正在加载工作流…')}
        className="pb-4"
      />
    )
  }

  if (kind === 'error') {
    return (
      <PageAsyncErrorState
        theme={theme}
        title={terminalish ? 'workflow_fetch_failed' : '工作流列表加载失败'}
        detail={detail}
        onRetry={onRetry}
        illustration={<EmptyWorkflowIllustration angular={angular} />}
      />
    )
  }

  return (
    <PageAsyncEmptyState
      illustration={<EmptyWorkflowIllustration angular={angular} />}
      title="还没有工作流"
      description="先选择类型，再在画布里编排节点与连线"
      action={
        onCreate ? (
          <ChromePrimaryButton density="compact" onClick={onCreate}>
            新建工作流
          </ChromePrimaryButton>
        ) : undefined
      }
    />
  )
}
